import { z } from 'zod';

import type { QuizAnswers as RecordAnswers } from '@/pages/MyYachts/components/modals/QuizModal/quizModal.data.ts';
import { getVesselQuestionnaireAnswers } from '@/pages/MyYachts/components/modals/vesselQuestionnaire.data.ts';
import type { UpdateVesselDto, Vessel } from '@/store/vessels/vessel.dto';
import { DEFAULT_TIME_ZONE, getBrowserTimeZone, isValidTimeZone } from '@/utils/timezone';
import { round1, toCanonicalFt, toDisplayLength, type LengthUnit } from '@/utils/unitConversion';

export const vesselQuestionsAnswersSchema = z.object({
  draft_m: z.number().optional().catch(undefined),
  beam_m: z.number().optional().catch(undefined),
  guest_cabins: z.number().optional().catch(undefined),
  crew_count: z.number().optional().catch(undefined),
  country_built: z.string().optional().catch(undefined),
  home_port: z.string().optional().catch(undefined),
  engines: z.string().optional().catch(undefined),
  notes: z.string().optional().catch(undefined),
  cruising_areas: z
    .array(z.enum(['USA East Coast', 'USA West Coast', 'Bahamas & Caribbean']))
    .optional()
    .catch(undefined),
  usage_type: z.enum(['Private', 'Charter', 'Both']).optional().catch(undefined),
  flag_state: z.string().optional().catch(undefined),
  vessel_type: z
    .array(
      z.enum([
        'Motoryacht',
        'Sailing yacht',
        'Center Console',
        'Day Boat',
        'Trawler',
        'Sportfish Yacht',
        'Sailboat',
        'Powerboat',
        'Cruiser',
        'Commercial Vessel',
      ]),
    )
    .optional()
    .catch(undefined),
  hull_material: z
    .array(z.enum(['Fiberglass', 'Aluminum', 'Steel', 'Wood']))
    .optional()
    .catch(undefined),
  estimated_value: z.number().optional().catch(undefined),
});

export const vesselFormSchema = z.object({
  vesselName: z.string().min(1, 'Please enter a vessel name.'),
  vesselMake: z.string().optional(),
  vesselModel: z.string().optional(),
  yearBuilt: z
    .string()
    .optional()
    .refine((val) => !val || /^\d{4}$/.test(val), 'Please enter the year as 4 digits.'),
  lengthOverall: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\d+(\.\d+)?$/.test(val),
      'Please enter the length as a positive number.',
    ),
  countryRegistration: z.string().optional(),
  registrationNumber: z.string().optional(),
  hullId: z.string().optional(),
  description: z.string().optional(),
  // Same check the backend runs with time.LoadLocation, so a bad zone fails here, not as a 400.
  timezone: z.string().refine(isValidTimeZone, 'Please select a timezone.'),
  questions: vesselQuestionsAnswersSchema,
});

export type VesselFormValues = z.infer<typeof vesselFormSchema>;

export const VESSEL_DEFAULT_FORM_VALUES: VesselFormValues = {
  vesselName: '',
  vesselMake: '',
  vesselModel: '',
  yearBuilt: '',
  lengthOverall: '',
  countryRegistration: '',
  registrationNumber: '',
  hullId: '',
  description: '',
  // New vessels start from the creator's own zone; existing ones keep whatever is stored.
  timezone: getBrowserTimeZone(),
  questions: {},
};

export const vesselToFormValues = (
  vessel: Vessel | undefined,
  lengthUnit: LengthUnit = 'ft',
): VesselFormValues => ({
  vesselName: vessel?.vessel_name || '',
  vesselMake: vessel?.make || '',
  vesselModel: vessel?.model || '',
  yearBuilt: vessel?.year_built?.toString() || '',
  lengthOverall:
    vessel?.length_overall != null
      ? String(round1(toDisplayLength(vessel.length_overall, lengthUnit)))
      : '',
  countryRegistration: vessel?.country_registration || '',
  registrationNumber: vessel?.registration_number || '',
  hullId: vessel?.structure_number_value || '',
  description: vessel?.description || '',
  timezone: vessel?.timezone || DEFAULT_TIME_ZONE,
  questions: vesselQuestionsAnswersSchema.parse(getVesselQuestionnaireAnswers(vessel?.questions)),
});

export const formValuesToUpdatePayload = (
  values: VesselFormValues,
  lengthUnit: LengthUnit = 'ft',
): UpdateVesselDto => ({
  vessel_name: values.vesselName,
  make: values.vesselMake || undefined,
  model: values.vesselModel || undefined,
  year_built: values.yearBuilt ? parseInt(values.yearBuilt, 10) : undefined,
  length_overall: values.lengthOverall
    ? Math.round(toCanonicalFt(parseFloat(values.lengthOverall), lengthUnit))
    : undefined,
  country_registration: values.countryRegistration || undefined,
  registration_number: values.registrationNumber || undefined,
  structure_number_type: values.hullId ? 'hull_id' : undefined,
  structure_number_value: values.hullId || undefined,
  description: values.description?.trim() || undefined,
  // Always sent — see UpdateVesselDto.timezone; omitting it resets the vessel to UTC.
  timezone: values.timezone || DEFAULT_TIME_ZONE,
  questions: Object.values(values.questions).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== undefined,
  )
    ? {
        version: 2,
        answers: values.questions,
      }
    : undefined,
});

const trimmedOrUndefined = (value?: string) => value?.trim() || undefined;

type VesselRecordFields = Pick<
  RecordAnswers,
  | 'make'
  | 'model'
  | 'year_built'
  | 'length_overall'
  | 'registration_number'
  | 'hull_id'
  | 'description'
>;

export const withVesselRecordAnswers = <T extends object>(
  vessel: Vessel | null | undefined,
  answers: T,
): T & VesselRecordFields => {
  const current = answers as VesselRecordFields;

  return {
    ...answers,
    make: vessel?.make ?? current.make,
    model: vessel?.model ?? current.model,
    year_built: vessel?.year_built ?? current.year_built,
    length_overall: vessel?.length_overall ?? current.length_overall,
    registration_number: vessel?.registration_number ?? current.registration_number,
    hull_id: vessel?.structure_number_value ?? current.hull_id,
    description: vessel?.description ?? current.description,
  };
};

export const vesselUpdateFromAnswers = (
  vessel: Vessel,
  answers: VesselRecordFields,
): UpdateVesselDto => {
  const hullId = trimmedOrUndefined(answers.hull_id) ?? vessel.structure_number_value;

  return {
    vessel_name: vessel.vessel_name,
    make: trimmedOrUndefined(answers.make) ?? vessel.make,
    model: trimmedOrUndefined(answers.model) ?? vessel.model,
    year_built: answers.year_built ?? vessel.year_built,
    length_overall:
      answers.length_overall != null ? Math.round(answers.length_overall) : vessel.length_overall,
    country_registration: vessel.country_registration,
    registration_number:
      trimmedOrUndefined(answers.registration_number) ?? vessel.registration_number,
    structure_number_type: hullId ? 'hull_id' : vessel.structure_number_type,
    structure_number_value: hullId,
    description: trimmedOrUndefined(answers.description) ?? vessel.description,
    home_port: vessel.home_port,
    timezone: vessel.timezone || DEFAULT_TIME_ZONE,
    questions: { version: 2, answers: vesselQuestionsAnswersSchema.parse(answers) },
  };
};

export const recordAnswersToFormValues = (
  answers: VesselRecordFields,
  lengthUnit: LengthUnit = 'ft',
): Partial<VesselFormValues> => ({
  ...(trimmedOrUndefined(answers.make) ? { vesselMake: answers.make } : {}),
  ...(trimmedOrUndefined(answers.model) ? { vesselModel: answers.model } : {}),
  ...(answers.year_built != null ? { yearBuilt: String(answers.year_built) } : {}),
  ...(answers.length_overall != null
    ? { lengthOverall: String(round1(toDisplayLength(answers.length_overall, lengthUnit))) }
    : {}),
  ...(trimmedOrUndefined(answers.registration_number)
    ? { registrationNumber: answers.registration_number }
    : {}),
  ...(trimmedOrUndefined(answers.hull_id) ? { hullId: answers.hull_id } : {}),
  ...(trimmedOrUndefined(answers.description) ? { description: answers.description } : {}),
});

export const formValuesToRecordAnswers = (
  values: VesselFormValues,
  lengthUnit: LengthUnit = 'ft',
): VesselRecordFields => ({
  make: trimmedOrUndefined(values.vesselMake),
  model: trimmedOrUndefined(values.vesselModel),
  year_built: values.yearBuilt ? parseInt(values.yearBuilt, 10) : undefined,
  length_overall: values.lengthOverall
    ? Math.round(toCanonicalFt(parseFloat(values.lengthOverall), lengthUnit))
    : undefined,
  registration_number: trimmedOrUndefined(values.registrationNumber),
  hull_id: trimmedOrUndefined(values.hullId),
  description: trimmedOrUndefined(values.description),
});
